/**
 * Outdoor Rectangular Area Template
 *
 * Flat, open-air areas (ramp or tiedown) without walls or doors.
 * Renders as a bordered rectangle with optional semi-transparent floor fill.
 * Single template serves both ramp_area and tiedown_area via area_subtype.
 */

import { z } from 'zod'
import { ParkingSquare } from 'lucide-react'
import type {
  BuildingTemplate,
  BaseTemplateParams,
  GeneratedBuilding,
  GeneratedUnit,
} from './types'

/** Area subtype determines DB type and unit type. `exit_point` = taxiway
 *  exit/entrance area, `fuel_depot` = fuel farm — both non-allocatable, no unit. */
export type OutdoorAreaSubtype = 'ramp_area' | 'tiedown_area' | 'exit_point' | 'fuel_depot'

/** Parameters for outdoor rectangular template */
export interface OutdoorRectangularParams extends BaseTemplateParams {
  width_ft: number
  depth_ft: number
  area_subtype: OutdoorAreaSubtype
  border_color: string
  border_style: 'solid' | 'dashed'
  border_width_ft: number     // Border strip width in feet (default 2)
  floor_color: string | null  // null = transparent/no fill
  floor_opacity: number       // 0-1 (0 = fully transparent, 1 = opaque)
}

/** Form schema for outdoor rectangular template */
export const outdoorRectangularFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name too long'),
  code: z.string().min(1, 'Area code is required').max(20, 'Code too long'),
  width_ft: z.number().min(20, 'Min 20 ft').max(2000, 'Max 2000 ft'),
  depth_ft: z.number().min(20, 'Min 20 ft').max(2000, 'Max 2000 ft'),
  area_subtype: z.enum(['ramp_area', 'tiedown_area', 'exit_point', 'fuel_depot']),
  border_color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color'),
  border_style: z.enum(['solid', 'dashed']),
  border_width_ft: z.number().min(0.5, 'Min 0.5 ft').max(10, 'Max 10 ft'),
  floor_color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color').nullable(),
  floor_opacity: z.number().min(0).max(1),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  bearing_deg: z.number().transform(v => ((v % 360) + 360) % 360),
})

/** Default values for outdoor rectangular template */
export const outdoorRectangularDefaultValues: OutdoorRectangularParams = {
  name: 'New Ramp Area',
  code: '',
  width_ft: 200,
  depth_ft: 150,
  area_subtype: 'ramp_area',
  border_color: '#FFD700',
  border_style: 'solid',
  border_width_ft: 2,
  floor_color: '#FFD700',
  floor_opacity: 0.3,
  wall_color: '#A0A0A0',  // Required by BaseTemplateParams, unused for outdoor
  latitude: 0,
  longitude: 0,
  bearing_deg: 0,
}

/** Generate building configuration from parameters */
function generateBuilding(params: OutdoorRectangularParams): GeneratedBuilding {
  const rotationRadians = (params.bearing_deg * Math.PI) / 180

  return {
    name: params.name,
    code: params.code,
    type: params.area_subtype,  // 'ramp_area' or 'tiedown_area'
    exterior_width: params.width_ft,
    exterior_depth: params.depth_ft,
    exterior_height: 0,  // Flat, no height
    latitude: params.latitude,
    longitude: params.longitude,
    rotation: rotationRadians,
    door_width: 0,
    door_height: 0,
    settings: {
      floor_color: params.floor_color,
      wall_color: params.wall_color,
      border_color: params.border_color,
      border_style: params.border_style,
      border_width_ft: params.border_width_ft,
      floor_opacity: params.floor_opacity,
    },
    type_config: {
      template: 'outdoor_rectangular',
      area_subtype: params.area_subtype,
    },
  }
}

/** Generate units for the outdoor area (single unit; exit areas have none) */
function generateUnits(
  _building: GeneratedBuilding,
  params: OutdoorRectangularParams
): GeneratedUnit[] {
  // Exit + fuel-depot areas are non-allocatable infrastructure — no units.
  if (params.area_subtype === 'exit_point' || params.area_subtype === 'fuel_depot') return []

  const unitType = params.area_subtype === 'ramp_area' ? 'ramp' : 'tiedown'

  return [
    {
      code: params.code,
      type: unitType,
      capacity_strategy: 'area_based',
      interior_width: params.width_ft,
      interior_depth: params.depth_ft,
      interior_height: 0,
      door_width: null,
      door_height: null,
      offset_x: 0,
      offset_y: 0,
      unit_rotation: 0,
      edges: [],  // No walls
    },
  ]
}

/** Outdoor Rectangular Area Template */
export const outdoorRectangularTemplate: BuildingTemplate<OutdoorRectangularParams> = {
  id: 'outdoor_rectangular',
  name: 'Outdoor Rectangular Area',
  description: 'Flat open-air area for ramp or tiedown parking.',
  icon: ParkingSquare,
  buildingType: 'ramp_area',  // Default, overridden by area_subtype at generate time
  generateBuilding,
  generateUnits,
  formSchema: outdoorRectangularFormSchema,
  defaultValues: outdoorRectangularDefaultValues,
}
